import moment from 'moment';
import _some from 'lodash/some';
import convertCurrencyFormat from '../../util/currencyFormatter';

/**
 * Returns a product image of a selected size
 * @param {*} productLook the product look
 * @param {string} imageSize the product image size
 * @param {boolean} isRectangularImage - if true look up rectangular image
 */
export function getProductImage(productLook, imageSize, isRectangularImage) {
  const squareImage =
    productLook &&
    productLook.images &&
    productLook.images[0] &&
    productLook.images[0].aspect_ratios['1x1'] &&
    productLook.images[0].aspect_ratios['1x1'][imageSize];
  let rectangularImage;
  if (isRectangularImage) {
    const aspectRatios =
      productLook &&
      productLook.images &&
      productLook.images[0] &&
      productLook.images[0].aspect_ratios;
    const rectangularRatio = aspectRatios && (aspectRatios['3x4'] || aspectRatios['2x3']); // check for 3x4 ratio first, then fall back to deprecated 2x3 ratio
    rectangularImage = (rectangularRatio && rectangularRatio[imageSize]) || squareImage; // use square image if we don't have a true rectangular one
  }
  return isRectangularImage ? rectangularImage : squareImage;
}

/**
 * Gets the category for a product, if available.
 * Returns null if none is available.
 * @param {*} product the product
 */
export function getProductCategory(product) {
  const categoryChain = product.category_chain;
  if (categoryChain && categoryChain.length) {
    return categoryChain[categoryChain.length - 1];
  }

  return null;
}

export const formatDate = dateString => {
  if (!dateString) return null;
  return moment.utc(dateString).format('M/D');
};

export const formatExpShippingDate = (expShippingMin, expShippingMax) => {
  const expShipText = 'Expedited Shipping:';
  if (expShippingMin === expShippingMax) {
    return `${expShipText} ${expShippingMin}`;
  }
  return `${expShipText} ${expShippingMin} - ${expShippingMax}`;
};

export const formatPrice = (centsPrice, forSegment, fractionDigits) => {
  if (typeof centsPrice !== 'number') {
    return null;
  }

  let minPriceFractionDigits;

  if (typeof fractionDigits !== 'number') {
    // hide decimals if cents are zero and fractionDigits is not defined
    minPriceFractionDigits = (centsPrice / 100) % 1 > 0 ? 2 : 0;
  } else {
    minPriceFractionDigits = fractionDigits;
  }

  const formattedPrice = convertCurrencyFormat(centsPrice / 100, 'USD', minPriceFractionDigits, 2);

  if (forSegment) {
    // slice off the dollar sign and convert for tracking
    return Number(formattedPrice.slice(1));
  }

  return formattedPrice;
};

export const formatZolaPriceRange = priceRange => {
  if (priceRange.min_price === priceRange.max_price) {
    const singlePrice = formatPrice(priceRange.min_price);
    return `${singlePrice}`;
  }
  const minPrice = formatPrice(priceRange.min_price);
  const maxPrice = formatPrice(priceRange.max_price);
  return `${minPrice} - ${maxPrice}`;
};

export const findSkuById = (skus, skuId) => {
  if (!skuId) {
    return null;
  }

  const filteredSkus = skus.filter(sku => sku.id === skuId);
  return filteredSkus[0];
};

export const getShipDates = activeSku => {
  if (!activeSku) {
    return null;
  }

  const arrivalRange = activeSku.arrival_range;
  if (!arrivalRange) return {};
  const expArrivalRange = activeSku.expedited_arrival_range;
  const freeShippingMin = formatDate(arrivalRange.min);
  const freeShippingMax = formatDate(arrivalRange.max);
  const expShippingMin = expArrivalRange && formatDate(expArrivalRange.min);
  const expShippingMax = expArrivalRange && formatDate(expArrivalRange.max);
  return {
    freeShippingMin,
    freeShippingMax,
    expShippingMin,
    expShippingMax,
  };
};

export function findSwatchByProductLookId(id, swatches) {
  const filteredSwatches = swatches.filter(swatch => swatch.product_look_id === id);
  return filteredSwatches[0];
}

export function findProductLookById(id, productLooks) {
  const filteredProductLooks = productLooks.filter(productLook => productLook.id === id);
  return filteredProductLooks[0];
}

export function findProductLookByKey(key, productLooks) {
  const filteredProductLooks = productLooks.filter(productLook => productLook.key === key);
  return filteredProductLooks[0];
}

export function findSkuIndexById(id, skus) {
  let index = 0;
  skus.forEach((sku, i) => {
    if (sku.id === id) index = i;
  });
  return index;
}

export function buildSkuIdIndexMap(skus) {
  const map = {};
  skus.forEach((sku, i) => {
    map[sku.id] = i;
    map[i] = sku.id;
  });
  return map;
}

export const isProductFree = (activeProductLook, activeSku) => {
  const productLook =
    activeProductLook &&
    activeProductLook.price_range.min_price === 0 &&
    activeProductLook.price_range.max_price === 0;
  const sku = activeSku && activeSku.price_cents === 0;
  return productLook || sku;
};

export const hidePriceMatchingCopy = (activeProductLook, activeSku, productType) => {
  if (productType === 'EXPERIENCE' || productType === 'GIFT_CARD') return true;
  return isProductFree(activeProductLook, activeSku);
};

export const getProductPrices = (activeProductLook, productAttributes, productData, activeSku) => {
  const productPrices = {};
  if (activeSku) {
    // determine reference price from SKU
    if (activeSku.reference_price && activeSku.reference_price.type === 'MSRP') {
      productPrices.msrp = formatPrice(activeSku.reference_price.amount);
    } else if (activeSku.reference_price && activeSku.reference_price.type === 'COMPAREATPRICE') {
      productPrices.sale = formatPrice(activeSku.reference_price.amount);
    }

    // determine actual price from SKU
    let priceCopy = productPrices.sale ? 'Sale' : '';
    priceCopy = productPrices.msrp ? 'Our Price' : priceCopy;
    productPrices.zolaPrice = `${priceCopy} ${formatPrice(activeSku.price_cents)}`;
  } else {
    // determine reference price range from product look
    if (
      activeProductLook.reference_price_range &&
      activeProductLook.reference_price_range.type === 'MSRP'
    ) {
      productPrices.msrp = formatZolaPriceRange(activeProductLook.reference_price_range);
    } else if (
      activeProductLook.reference_price_range &&
      activeProductLook.reference_price_range.type === 'COMPAREATPRICE'
    ) {
      productPrices.sale = formatZolaPriceRange(activeProductLook.reference_price_range);
    }

    // determine actual price range from product look
    let priceCopy = productPrices.sale ? 'Sale' : '';
    priceCopy = productPrices.msrp ? 'Our Price' : priceCopy;
    productPrices.zolaPrice = `${priceCopy} ${formatZolaPriceRange(activeProductLook.price_range)}`;
  }
  // special free pricing for invites sampler pack
  if (isProductFree(activeProductLook, activeSku)) {
    productPrices.zolaPrice = 'Free';
  }
  return productPrices;
};

export const getProductViewedEventData = (productData, activeProductLook, activeSku, quantity) => {
  const { brand } = productData;
  const categoryView = getProductCategory(productData);
  const skuToTrack = activeSku || activeProductLook.sku_previews[0]; // we might not have an active SKU at this point, so fall back to the first one
  return {
    product_id: productData.id,
    sku: skuToTrack.id,
    brand: brand.name,
    name: productData.name,
    category: categoryView && categoryView.name,
    variant: activeProductLook.id,
    quantity,
    url: window.location.href,
    image_url: getProductImage(activeProductLook, 'medium'),
    price: formatPrice(skuToTrack.price_cents, true), // flag to format for segment
    location: 'PDP',
  };
};

export const getProductAddedEventData = (
  productData,
  activeProductLook,
  activeSku,
  quantity,
  cartId,
  location,
  section,
  extraData
) => {
  const { brand } = productData;
  return {
    product_id: productData.id,
    sku: activeSku.id,
    brand: brand && brand.name,
    name: productData.name,
    variant: activeProductLook.id,
    quantity,
    image_url: getProductImage(activeProductLook, 'medium'),
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location: location || 'PDP',
    section: section || 'MAIN_CTA',
    cart_id: cartId,
    ...extraData,
  };
};

export const getKitProductAddedEventData = (
  productData,
  activeProductLook,
  activeSku,
  quantity,
  cartId,
  location,
  section,
  position
) => {
  const { brand } = productData;
  return {
    product_id: productData.id,
    sku: activeSku.id,
    brand: brand && brand.name,
    name: productData.name,
    variant: activeProductLook.id,
    quantity,
    image_url: getProductImage(activeProductLook, 'medium'),
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location,
    section,
    cart_id: cartId,
    position,
  };
};

export const getRegistryItemAddedEventData = ({
  productData,
  activeProductLook,
  activeSku,
  quantity,
  location,
  section,
  extraData,
}) => {
  const { brand } = productData;
  return {
    sku: activeSku.id,
    name: productData.name,
    brand: brand && brand.name,
    quantity,
    image_url: getProductImage(activeProductLook, 'medium'),
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location: location || 'PDP',
    section: section || 'MAIN_CTA',
    ...extraData,
  };
};

export const getKitRegistryItemAddedEventData = ({
  productData,
  activeProductLook,
  activeSku,
  quantity,
  location,
  section,
  extraData = {},
}) => {
  const { brand } = productData;
  const eventData = {
    sku: activeSku.id,
    name: productData.name,
    brand: brand && brand.name,
    quantity,
    image_url: getProductImage(activeProductLook, 'medium'),
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location,
    section,
    ...extraData,
  };
  return eventData;
};

export const getProductListViewedEventData = ({
  products,
  sectionOverride = '',
  location,
  type,
  endpointVariant,
}) => {
  let section = '';
  switch (type) {
    case 'brand-related':
      section = 'BRAND';
      break;
    case 'frequently-added-together-skus':
      section = 'RECOMMENDATION_FREQUENTLY_ADDED_TOGETHER';
      break;
    case 'also-added-skus':
      section = 'RECOMMENDATION_ALSO_ADDED';
      break;
    default:
      section = sectionOverride;
      break;
  }
  const productsData = {};
  products.forEach((product, index) => {
    const productLook = product.product_look_views[0];
    const sku = productLook.sku_previews[0];
    productsData[index] = {
      productId: product.id,
      skuId: sku.id,
      productLookKey: productLook.key,
      name: product.name,
      brandName: product.brand && product.brand.name,
      image: getProductImage(productLook, 'medium'),
      price: formatPrice(sku.price_cents, true),
      quantity: product.suggested_quantity,
    };
  });
  return {
    endpoint_variant: endpointVariant || 'N/A',
    location,
    section,
    products: productsData,
  };
};

/**
 * Checks whether a given SKU matches all of the selected attributes.
 *
 * Note that all of the SKU's attributes don't have to match the selections,
 * but all the selections must match the SKU's attributes.
 */
const skuMatchesSelectedAttributes = (sku, selectedAttributes) => {
  // The SKU does NOT match if any of the selected attributes have a different value than the SKU has for that attribute key.
  // If there are no selected attributes at all, the SKU will match.
  // If the SKU does not have a value for one of the selected attributes, it will not match.
  const skuDoesNotMatch = _some(Object.keys(selectedAttributes), selectedAttributeKey => {
    const selectedAttributeValue = selectedAttributes[selectedAttributeKey];
    const matchingSkuAttribute = sku.attribute_values.filter(
      skuAttr => skuAttr.key === selectedAttributeKey
    )[0]; // if filter returns an empty array, index 0 will be undefined
    return !matchingSkuAttribute || matchingSkuAttribute.value_key !== selectedAttributeValue;
  });

  return !skuDoesNotMatch;
};

/**
 * Determines which single SKU matches the given attribute selections.
 * If multiple SKUs in the list match, then none match as we are only looking for one match.
 */
export const resolveMatchingSku = (skus, selectedAttributes) => {
  const matchingSkus = skus.filter(sku => skuMatchesSelectedAttributes(sku, selectedAttributes));
  return matchingSkus.length === 1 ? matchingSkus[0] : null;
};

// Sets all attributes of a given sku using a given React useState setter
// Primary use is to pre-select all attributes when only one sku is available for a product look
export const setSkuAttributes = (attributeSetter, product, sku) => {
  if (product.attributes.length > 0) {
    const updatedAttributes = {};
    sku.attribute_values.forEach(attrib => {
      updatedAttributes[attrib.key] = attrib.value_key;
    });
    attributeSetter(updatedAttributes);
  }
};

// creates a map for all the query params in the url
// currently used for the PDP to determine what breadcrumbs to display
export const getUrlParams = (search = '') => {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  return hashes.reduce((acc, hash) => {
    const [key, val] = hash.split('=');
    return {
      ...acc,
      [key]: decodeURIComponent(val),
    };
  }, {});
};

// traverses the category tree for a given key and returns a chain of categories from parent to child
export const buildCategoryChainFromKey = (categoryKey, categoryTree, categoryChain) => {
  const results = categoryChain || [];
  // set base category
  if (results.length === 0) {
    results.push({ key: categoryTree.key, name: categoryTree.name });
  }
  if (categoryTree.key === categoryKey) {
    return results;
  }
  // recursively call function for each item in category children
  for (let i = 0; i < categoryTree.children.length - 1; i += 1) {
    const child = categoryTree.children[i];
    const chain = results.slice();
    chain.push({ name: child.name, key: child.key });
    const found = buildCategoryChainFromKey(categoryKey, child, chain);
    if (found) {
      return found;
    }
  }
  return null;
};
